import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Instagram, CheckCircle, AlertCircle, MoreVertical, Trash2, Activity, Zap, ZapOff, Clock, ChevronDown } from 'lucide-react';
import { instagramAccountService } from '../lib/supabaseServices';
import { ACCENT_COLOR_HEX } from '../lib/theme';

const AccountsPage = ({ darkMode }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Load accounts on component mount
  useEffect(() => {
    loadAccounts();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Add minimum loading time for smooth transitions
      const minLoadTime = new Promise(resolve => setTimeout(resolve, 800));
      const accountsPromise = instagramAccountService.getAccounts();
      
      const [data] = await Promise.all([accountsPromise, minLoadTime]);
      setAccounts(data);
    } catch (err) {
      console.error('Error loading accounts:', err);
      setError(err.message);
    } finally {
      // Add a slight delay before removing loading state for smooth transition
      setTimeout(() => {
        setLoading(false);
      }, 200);
    }
  };

  const handleDeleteAccount = (account) => {
    setAccountToDelete(account);
    setShowDeleteModal(true);
    setOpenDropdown(null);
  };

  const confirmDelete = async () => {
    try {
      await instagramAccountService.deleteAccount(accountToDelete.id);
      await loadAccounts(); // Reload accounts after deletion
      setShowDeleteModal(false);
      setAccountToDelete(null);
    } catch (err) {
      console.error('Error deleting account:', err);
      setError('Failed to delete account');
    }
  };

  const toggleAccountStatus = async (accountId, currentStatus) => {
    try {
      await instagramAccountService.toggleAccountStatus(accountId, !currentStatus);
      await loadAccounts(); // Reload to reflect changes
    } catch (err) {
      console.error('Error toggling account status:', err);
      setError('Failed to update account status');
    }
  };

  // Helper functions for session status
  const getSessionStatusColor = (status) => {
    if (darkMode) {
      switch(status) {
        case 'online': return 'bg-green-500/20 text-green-400 border-green-500/30';
        case 'away': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
        case 'offline': 
        default: return 'bg-red-500/20 text-red-400 border-red-500/30'; // Default to offline
      }
    } else {
      switch(status) {
        case 'online': return 'bg-green-100 text-green-800 border-green-200';
        case 'away': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        case 'offline':
        default: return 'bg-red-100 text-red-800 border-red-200'; // Default to offline
      }
    }
  };

  const getSessionStatusIcon = (status) => {
    switch(status) {
      case 'online': return <Activity className="w-3 h-3" />;
      case 'away': return <Clock className="w-3 h-3" />;
      case 'offline':
      default: return <Activity className="w-3 h-3 opacity-50" />; // Default to offline icon
    }
  };

  const getSessionStatusText = (status) => {
    switch(status) {
      case 'online': return 'Online';
      case 'away': return 'Away';
      case 'offline':
      default: return 'Offline'; // Default to offline instead of unknown
    }
  };

  const formatLastHeartbeat = (lastHeartbeat, timeSinceLastHeartbeat) => {
    if (!lastHeartbeat) return 'Never';
    
    if (timeSinceLastHeartbeat < 60) {
      return 'Just now';
    } else if (timeSinceLastHeartbeat < 3600) {
      return `${Math.floor(timeSinceLastHeartbeat / 60)}m ago`;
    } else if (timeSinceLastHeartbeat < 86400) {
      return `${Math.floor(timeSinceLastHeartbeat / 3600)}h ago`;
    } else {
      return new Date(lastHeartbeat).toLocaleDateString();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 lg:p-6"
    >
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
        <div>
          <h2 className={`text-xl lg:text-2xl font-bold ${darkMode ? 'text-primary' : 'text-gray-900'}`}>Instagram Accounts</h2>
          <p className={`${darkMode ? 'text-secondary' : 'text-gray-600'} mt-1`}>Manage your connected Instagram accounts</p>
        </div>
        <button className={`w-full sm:w-auto ${darkMode ? 'bg-gradient-to-r from-accent to-accent-hover shadow-lg shadow-accent/25' : 'bg-gradient-to-r from-accent to-accent-hover'} text-primary px-6 py-3 rounded font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2`}>
          <Plus className="w-5 h-5" />
          <span>Connect Account</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`mb-6 p-4 ${darkMode ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-red-50 border-red-200 text-red-700'} border rounded flex items-center space-x-2`}>
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        /* Loading Skeleton */
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className={`${darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'} rounded p-6 shadow-sm border animate-pulse`}>
              <div className="flex items-center space-x-4 mb-4">
                <div className={`w-16 h-16 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded-full`}></div>
                <div className="flex-1">
                  <div className={`w-32 h-5 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded mb-2`}></div>
                  <div className={`w-24 h-4 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded mb-2`}></div>
                  <div className={`w-20 h-4 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded`}></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Accounts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
            {accounts.map((account) => (
              <motion.div
                key={account.id}
                whileHover={{ y: -2 }}
                className={`${darkMode ? 'bg-surface border-border shadow-accent/5' : 'bg-white border-gray-200'} rounded p-4 lg:p-5 shadow-sm border relative`}
              >
                {/* Menu */}
                <div className="absolute top-4 right-4 flex items-center space-x-2">
                  <div className="relative">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdown(openDropdown === account.id ? null : account.id);
                      }}
                      className={`p-1 ${darkMode ? 'text-secondary hover:text-primary' : 'text-gray-400 hover:text-gray-600'} transition-colors`}
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {openDropdown === account.id && (
                      <div className={`absolute right-0 top-8 w-48 ${darkMode ? 'bg-elevated border-border' : 'bg-white border-gray-200'} rounded shadow-lg border z-10`}>
                        <div className="py-1">
                          <button
                            onClick={() => handleDeleteAccount(account)}
                            className={`w-full text-left px-4 py-2 text-sm ${darkMode ? 'text-red-400 hover:bg-surface' : 'text-red-600 hover:bg-gray-50'} transition-colors flex items-center space-x-2`}
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete Account</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Account Info */}
                <div className="flex items-center space-x-3 mb-3 pr-8">
                  <div className="relative">
                  <img
                      src={account.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(account.username)}&background=${ACCENT_COLOR_HEX}&color=ffffff&size=64`}
                    alt={account.username}
                    className="w-12 h-12 rounded-full object-cover"
                      onError={(e) => {
                        // Fallback to generated avatar if Instagram image fails to load
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(account.username)}&background=${ACCENT_COLOR_HEX}&color=ffffff&size=64`;
                      }}
                  />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-semibold ${darkMode ? 'text-primary' : 'text-gray-900'} truncate`}>
                      {account.display_name || account.username}
                    </h3>
                    <p className={`${darkMode ? 'text-secondary' : 'text-gray-600'} text-sm`}>@{account.username}</p>
                  </div>
                </div>

                {/* Session Status Info */}
                <div className={`mb-3 p-3 ${darkMode ? 'bg-elevated/50' : 'bg-gray-50'} rounded border ${darkMode ? 'border-border' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {getSessionStatusIcon(account.session?.status || 'offline')}
                      <span className={`text-sm font-medium ${darkMode ? 'text-primary' : 'text-gray-900'}`}>
                        Browser Status
                      </span>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${getSessionStatusColor(account.session?.status || 'offline')}`}>
                      {getSessionStatusText(account.session?.status || 'offline')}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        {account.session?.automationEnabled ? (
                          <Zap className={`w-3 h-3 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                        ) : (
                          <ZapOff className={`w-3 h-3 ${darkMode ? 'text-muted' : 'text-gray-500'}`} />
                        )}
                        <span className={`text-xs ${darkMode ? 'text-secondary' : 'text-gray-700'}`}>
                          Automation {account.session?.automationEnabled ? 'ON' : 'OFF'}
                        </span>
                      </div>
                      
                      {account.session?.extensionVersion && (
                        <span className={`text-xs ${darkMode ? 'text-muted' : 'text-gray-500'}`}>
                          v{account.session.extensionVersion}
                        </span>
                      )}
                  </div>
                    
                    <span className={`text-xs ${darkMode ? 'text-muted' : 'text-gray-500'}`}>
                      {formatLastHeartbeat(account.session?.lastHeartbeat, account.session?.timeSinceLastHeartbeat)}
                    </span>
                  </div>
                </div>

                {/* Message Statistics */}
                <div className={`mb-3 p-3 ${darkMode ? 'bg-elevated' : 'bg-gray-50'} rounded space-y-2`}>
                  {/* Total Messages */}
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-700'}`}>Total Messages Sent</span>
                    <span className={`text-sm font-semibold ${darkMode ? 'text-accent' : 'text-accent'}`}>
                      {account.total_messages_sent || 0}
                    </span>
                  </div>
                  
                  {/* Daily Messages with Progress Bar */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-700'}`}>Today's Messages</span>
                      <span className={`text-sm font-medium ${
                        account.messages_sent_today >= 50 
                          ? (darkMode ? 'text-red-400' : 'text-red-600')
                          : (darkMode ? 'text-primary' : 'text-gray-900')
                      }`}>
                        {account.messages_sent_today || 0} / 50
                      </span>
                    </div>
                    <div className={`w-full ${darkMode ? 'bg-border' : 'bg-gray-200'} rounded-full h-2`}>
                      <div 
                        className={`${
                          account.messages_sent_today >= 50
                            ? 'bg-gradient-to-r from-red-500 to-orange-500'
                            : account.messages_sent_today >= 40
                            ? 'bg-gradient-to-r from-yellow-400 to-orange-500'
                            : darkMode 
                            ? 'bg-gradient-to-r from-accent to-accent-hover' 
                            : 'bg-gradient-to-r from-accent to-accent-hover'
                        } h-2 rounded-full transition-all duration-300`}
                        style={{ 
                          width: `${Math.min((account.messages_sent_today / 50) * 100, 100)}%` 
                        }}
                      ></div>
                    </div>
                    {account.messages_sent_today >= 50 && (
                      <p className={`text-xs mt-1 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                        Daily limit reached - will resume tomorrow
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`${darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'} rounded p-6 max-w-md w-full mx-4 border`}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className={`p-2 ${darkMode ? 'bg-red-500/20' : 'bg-red-100'} rounded-full`}>
                <Trash2 className={`w-5 h-5 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
              </div>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-primary' : 'text-gray-900'}`}>
                Delete Account
              </h3>
            </div>
            
            <p className={`${darkMode ? 'text-secondary' : 'text-gray-600'} mb-6`}>
              Are you sure you want to delete <strong>@{accountToDelete?.username}</strong>? This action cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className={`flex-1 px-4 py-2 ${darkMode ? 'bg-elevated text-secondary hover:bg-surface' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-primary rounded hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default AccountsPage;